// ============================================
// Captain Downstream Handoff Service — CAP.17
//
// Persistence layer between Captain and Nurse.
// Captain writes CaptainResponse to Firestore.
// Nurse listens via onSnapshot — Firestore IS the handoff AND notification.
//
// Use Cases (Spec Gate):
//   UC-1: Successful extraction → Nurse picks up instantly via onSnapshot
//   UC-2: Partial extraction → extractionStatus 'partial', Nurse presents high-confidence results
//   UC-3: Extraction fails → extractionStatus 'failed', failureReason populated
//   UC-4: Firestore write fails → hard error to patient, no silent data loss
//   UC-5: Nurse listener disconnected → onSnapshot replays missed events on reconnect
// ============================================

import {
  collection,
  doc,
  setDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { C_CAPTAIN_RESULTS } from '../../constants/collections';
import {
  CAPTAIN_SCHEMA_VERSION,
} from '../../types/captainPipeline';
import type {
  CaptainResponse,
  CaptainResultDocument,
  ExtractionStatus,
} from '../../types/captainPipeline';

// ── Input Validation ──

/**
 * Validates a CaptainResponse payload before Firestore persistence.
 *
 * Required fields: patientId (non-empty string), biomarkers (non-empty array
 * for 'complete' status), each biomarker must have name, value, unit, confidence.
 * Malformed CaptainResponse is rejected with INVALID_PAYLOAD error.
 *
 * Returns an array of validation error strings. Empty array = valid.
 */
export function validateCaptainResponse(
  response: unknown,
  patientId: string,
  extractionStatus: ExtractionStatus,
  failureReason?: string,
): string[] {
  const errors: string[] = [];

  if (!patientId || typeof patientId !== 'string') {
    errors.push('patientId is required and must be a non-empty string');
  }

  // c5: failureReason must be populated when extractionStatus is 'failed'
  if (extractionStatus === 'failed' && (!failureReason || typeof failureReason !== 'string')) {
    errors.push('failureReason is required when extractionStatus is "failed"');
  }

  if (!response || typeof response !== 'object') {
    errors.push('CaptainResponse is required and must be an object');
    return errors; // Can't validate further
  }

  const r = response as Record<string, unknown>;

  if (typeof r.schemaVersion !== 'string' || !r.schemaVersion) {
    errors.push('CaptainResponse.schemaVersion is required');
  } else if (r.schemaVersion !== CAPTAIN_SCHEMA_VERSION) {
    errors.push(
      `CaptainResponse.schemaVersion "${r.schemaVersion}" does not match expected "${CAPTAIN_SCHEMA_VERSION}"`,
    );
  }

  if (typeof r.success !== 'boolean') {
    errors.push('CaptainResponse.success must be a boolean');
  }

  if (!Array.isArray(r.biomarkers)) {
    errors.push('CaptainResponse.biomarkers must be an array');
  } else {
    if (extractionStatus === 'complete' && r.biomarkers.length === 0) {
      errors.push(
        'CaptainResponse.biomarkers must be non-empty when extractionStatus is "complete"',
      );
    }
    for (let i = 0; i < r.biomarkers.length; i++) {
      const bm = r.biomarkers[i] as Record<string, unknown> | undefined;
      if (!bm || typeof bm !== 'object') {
        errors.push(`biomarkers[${i}] must be an object`);
        continue;
      }
      if (typeof bm.name !== 'string' || !bm.name) {
        errors.push(`biomarkers[${i}].name is required`);
      }
      if (typeof bm.value !== 'number') {
        errors.push(`biomarkers[${i}].value must be a number`);
      }
      if (typeof bm.unit !== 'string' || !bm.unit) {
        errors.push(`biomarkers[${i}].unit is required`);
      }
      if (typeof bm.confidence !== 'number') {
        errors.push(`biomarkers[${i}].confidence must be a number`);
      }
    }
  }

  return errors;
}

// ── Firestore Write with Verification ──

/**
 * Persists a CaptainResponse to Firestore at /captain_results/{sessionId}.
 *
 * Validates the payload before writing. Write success is confirmed via
 * Firestore response. Write failure throws a patient-safe error message.
 *
 * UC-1: On success, Nurse's onSnapshot fires instantly.
 * UC-4: On write failure, throws "Unable to save your results. Please try again."
 */
export async function persistCaptainResult(
  sessionId: string,
  patientId: string,
  captainResponse: CaptainResponse,
  extractionStatus: ExtractionStatus,
  failureReason?: string,
): Promise<void> {
  // Validate before persistence — malformed payloads never reach Firestore
  const validationErrors = validateCaptainResponse(
    captainResponse,
    patientId,
    extractionStatus,
    failureReason,
  );

  if (validationErrors.length > 0) {
    console.error(
      '[CAP.17] INVALID_PAYLOAD — CaptainResponse rejected before persistence',
      { sessionId, patientId, errors: validationErrors, payload: captainResponse },
    );
    throw new Error(`INVALID_PAYLOAD: ${validationErrors.join('; ')}`);
  }

  try {
    const docRef = doc(collection(db, C_CAPTAIN_RESULTS), sessionId);
    const now = Timestamp.now();

    await setDoc(docRef, {
      sessionId,
      patientId,
      captainResponse,
      extractionStatus,
      ...(failureReason !== undefined && { failureReason }),
      createdAt: now,
      updatedAt: now,
    });
    // Write confirmed — Nurse's onSnapshot will fire
  } catch (error) {
    console.error('[CAP.17] Firestore write failed', { sessionId, patientId, error });
    throw new Error('Unable to save your results. Please try again.');
  }
}

// ── Safe Document Parsing ──

/**
 * Parses a Firestore document snapshot into a CaptainResultDocument.
 * Returns null if the document data fails validation — never uses unsafe type assertions.
 *
 * This prevents runtime errors if the document schema deviates from the
 * client-side type definition (e.g., during schema migrations).
 */
export function parseCaptainResultDocument(
  data: Record<string, unknown> | undefined,
): CaptainResultDocument | null {
  if (!data) return null;

  // Validate required fields exist with correct types
  if (typeof data.sessionId !== 'string' || !data.sessionId) return null;
  if (typeof data.patientId !== 'string' || !data.patientId) return null;
  if (!data.captainResponse || typeof data.captainResponse !== 'object') return null;

  const status = data.extractionStatus;
  if (status !== 'complete' && status !== 'partial' && status !== 'failed') return null;

  if (!data.createdAt || typeof data.createdAt !== 'object') return null;
  if (!data.updatedAt || typeof data.updatedAt !== 'object') return null;

  // Validate captainResponse has required shape
  const cr = data.captainResponse as Record<string, unknown>;
  if (typeof cr.schemaVersion !== 'string') return null;
  if (typeof cr.success !== 'boolean') return null;
  if (!Array.isArray(cr.biomarkers)) return null;

  return {
    sessionId: data.sessionId,
    patientId: data.patientId,
    captainResponse: data.captainResponse as CaptainResponse,
    extractionStatus: status,
    ...(typeof data.failureReason === 'string' && { failureReason: data.failureReason }),
    createdAt: data.createdAt as CaptainResultDocument['createdAt'],
    updatedAt: data.updatedAt as CaptainResultDocument['updatedAt'],
  };
}

// ── Nurse onSnapshot Listener ──

/**
 * Subscribes to captain results for a specific patient via Firestore onSnapshot.
 *
 * Firestore IS the notification — no push protocol, no acknowledgment dance.
 * On reconnect, Firestore replays all missed changes natively (UC-5).
 *
 * The handler MUST be idempotent — onSnapshot may deliver both 'added' and
 * 'modified' events for the same document on reconnect.
 *
 * Returns an unsubscribe function to tear down the listener.
 */
export function subscribeToCaptainResults(
  patientId: string,
  handleCaptainResult: (result: CaptainResultDocument) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, C_CAPTAIN_RESULTS),
    where('patientId', '==', patientId),
  );

  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added' || change.type === 'modified') {
          const parsed = parseCaptainResultDocument(
            change.doc.data() as Record<string, unknown>,
          );
          if (parsed) {
            handleCaptainResult(parsed);
          } else {
            console.error(
              '[CAP.17] Failed to parse CaptainResultDocument — schema mismatch',
              { docId: change.doc.id, data: change.doc.data() },
            );
          }
        }
      });
    },
    (error) => {
      console.error('[CAP.17] onSnapshot listener error', { patientId, error });
      if (onError) {
        onError(error);
      }
    },
  );

  return unsubscribe;
}
